import clsx from 'clsx'
import OutstandingTab from 'components/billing/debtorNew/OutstandingTab'
import OverviewTab from 'components/billing/debtorNew/OverviewTab'
import Page from 'components/ui/Page'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

type TabId = 'overview' | 'outstanding'

const TABS: { id: TabId; label: string; hint: string }[] = [
    {
        id: 'overview',
        label: 'Monthly overview',
        hint: 'Invoiced vs open, status & payment funnel',
    },
    { id: 'outstanding', label: 'Outstanding debtors', hint: 'Open invoices grouped by provider' },
]

const DebtorNewPage = () => {
    // Active tab lives in the URL so views are shareable and survive a refresh.
    const [searchParams, setSearchParams] = useSearchParams()
    const activeTab: TabId = searchParams.get('tab') === 'outstanding' ? 'outstanding' : 'overview'
    const setActiveTab = (tab: TabId) => {
        setSearchParams(
            (prev) => {
                prev.set('tab', tab)
                return prev
            },
            { replace: true },
        )
    }
    const country = useSelector(
        (state: { countries: { countries: string[] } }) => state.countries.countries[0],
    )

    return (
        <Page title="Debtors">
            <div className="mx-auto max-w-screen-2xl px-1 py-6">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold text-dark">Debtors</h1>
                    {country && (
                        <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-md font-medium text-primary-700">
                            {country}
                        </span>
                    )}
                </div>
                <p className="mt-1 text-md text-neutral-500">
                    Overview of invoiced revenue, what is still open, and how payments are being
                    collected.
                </p>

                <div className="mt-6 flex gap-1 border-b border-neutral-200">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={clsx(
                                '-mb-px border-b-2 px-4 py-3 text-left transition',
                                activeTab === tab.id
                                    ? 'border-primary'
                                    : 'border-transparent hover:bg-neutral-50',
                            )}>
                            <span
                                className={clsx(
                                    'block text-md font-semibold',
                                    activeTab === tab.id ? 'text-primary' : 'text-neutral-700',
                                )}>
                                {tab.label}
                            </span>
                            <span className="block text-sm text-neutral-400">{tab.hint}</span>
                        </button>
                    ))}
                </div>

                <div className="mt-6">
                    {activeTab === 'overview' ? (
                        <OverviewTab country={country} />
                    ) : (
                        <OutstandingTab country={country} />
                    )}
                </div>
            </div>
        </Page>
    )
}

export default DebtorNewPage
